import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { RbacRoleEntity } from './rbac-role.entity';

export type ListOverrideAudience = 'organization' | 'role';

/**
 * A list override (PD-14278): what a Workd staffer CHANGED about a bundled
 * list for a tenant, never a copy of it. `changes` is a `ListDelta` from
 * `@supply-mover-2-0/core` — columns added/removed/reordered, filter chips
 * added/removed/reordered, filter-rule fields, sort and misc knobs — typed
 * loosely here to keep the entity free of a core dependency.
 *
 * Scope: `listId` null means the override applies to EVERY list of the
 * entity (`listKey`), including lists that do not exist yet; otherwise it
 * applies to that one bundled list. Audience: everyone in the tenant, or one
 * role. Resolution order and the role-ladder tiebreak live in
 * `libs/core/src/list-builder/overrides/resolve.ts`.
 *
 * One row per (listKey, listId, audience, role) — enforced by the unique
 * index so two overrides can never compete for the same slot.
 */
@Index(
	'idx_list_override_slot',
	['listKey', 'listId', 'audience', 'fkRbacRoleId'],
	{ unique: true },
)
@Index('idx_list_override_list_key', ['listKey'])
@Entity('list_override')
export class ListOverrideEntity extends BaseEntity {
	@Column('varchar', { name: 'listKey', length: 100 })
	public listKey!: string;

	/** Bundled list id, or NULL for an entity-wide override. */
	@Column('varchar', { name: 'listId', length: 100, nullable: true })
	public listId!: string | null;

	@Column('enum', {
		name: 'audience',
		enum: ['organization', 'role'],
		default: 'organization',
	})
	public audience!: ListOverrideAudience;

	/** Set when `audience` is 'role'; NULL for everyone. */
	@Column('int', { name: 'fk_rbac_role__id', unsigned: true, nullable: true })
	public fkRbacRoleId!: number | null;

	@ManyToOne(() => RbacRoleEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
		nullable: true,
	})
	@JoinColumn([{ name: 'fk_rbac_role__id', referencedColumnName: 'id' }])
	public fkRbacRole!: RbacRoleEntity | null;

	/** `ListDelta` — see the class doc. */
	@Column('json', { name: 'changes' })
	public changes!: Record<string, unknown>;
}
